import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import CalendarModalFrame from './CalendarModalFrame';
import {
  CalendarModalActions,
  CalendarModalButton,
  CalendarModalCheckbox,
  CalendarModalContent,
  CalendarModalField,
  CalendarModalStatus,
} from './CalendarModalPrimitives';
import {
  toCalendarModalApiRange,
  toCalendarModalFormRange,
  toTimedFormRangeFromAllDay,
} from './calendarModalRange';

const getFormRange = (eventData, isAllDay) => toCalendarModalFormRange({
  start: eventData?.start,
  end: eventData?.end,
  isAllDay,
});

function EventModal({ show, eventData, mode, onSave, onDelete, onClose, saving = false }) {
  const { t } = useTranslation();
  const initialAllDay = Boolean(eventData?.allDay);
  const initialRange = getFormRange(eventData, initialAllDay);
  const [title, setTitle] = useState(eventData?.title || '');
  const [reason, setReason] = useState(eventData?.reason || '');
  const [start, setStart] = useState(initialRange.start);
  const [end, setEnd] = useState(initialRange.end);
  const [isAllDay, setIsAllDay] = useState(initialAllDay);
  const isRestricted = mode === 'restricted' || Boolean(eventData?.is_restricted_period);

  useEffect(() => {
    const nextAllDay = Boolean(eventData?.allDay);
    const nextRange = getFormRange(eventData, nextAllDay);

    setTitle(eventData?.title || '');
    setReason(eventData?.reason || '');
    setStart(nextRange.start);
    setEnd(nextRange.end);
    setIsAllDay(nextAllDay);
  }, [eventData]);

  if (!show) return null;

  const handleAllDayChange = () => {
    const nextAllDay = !isAllDay;
    const nextRange = nextAllDay
      ? toCalendarModalFormRange({ start, end, isAllDay: true })
      : toTimedFormRangeFromAllDay({ start, end });

    setStart(nextRange.start);
    setEnd(nextRange.end);
    setIsAllDay(nextAllDay);
  };

  const handleSubmit = () => {
    if (isRestricted && !reason.trim()) {
      alert(t('calendar.modal.reasonRequired'));
      return;
    }

    if (!isRestricted && !title.trim()) {
      alert(t('calendar.modal.titleRequired'));
      return;
    }

    if (!start) {
      alert(t('calendar.modal.startRequired'));
      return;
    }

    if (!end) {
      alert(t('calendar.modal.endRequired'));
      return;
    }

    const apiRange = toCalendarModalApiRange({ start, end, isAllDay });
    if (!apiRange.start || !apiRange.end) {
      alert(t('calendar.modal.endAfterStart'));
      return;
    }

    const startTime = new Date(apiRange.start).getTime();
    const endTime = new Date(apiRange.end).getTime();

    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || startTime >= endTime) {
      alert(t('calendar.modal.endAfterStart'));
      return;
    }

    onSave(isRestricted
      ? {
        reason,
        start: apiRange.start,
        end: apiRange.end,
        is_restricted_period: true,
        is_all_day: isAllDay,
      }
      : {
        title,
        start_time: apiRange.start,
        end_time: apiRange.end,
        is_all_day: isAllDay,
      });
  };

  const isEditing = Boolean(eventData?.id);
  const modalTitle = t(isRestricted
    ? isEditing ? 'calendar.modal.restrictedEditTitle' : 'calendar.modal.restrictedAddTitle'
    : isEditing ? 'calendar.modal.eventEditTitle' : 'calendar.modal.eventAddTitle');
  const submitLabel = saving
    ? t('calendar.modal.saving')
    : isEditing
      ? t('calendar.modal.saveChanges')
      : t(isRestricted ? 'calendar.modal.addRestriction' : 'calendar.modal.addEvent');
  const startLabel = t(isAllDay ? 'calendar.modal.startDate' : 'calendar.modal.startTime');
  const endLabel = t(isAllDay ? 'calendar.modal.endDate' : 'calendar.modal.endTime');

  return (
    <CalendarModalFrame title={modalTitle} onClose={onClose}>
      <CalendarModalContent>
        {isRestricted ? (
          <CalendarModalField id="event-modal-reason" label={t('calendar.modal.reason')}>
            <input
              type="text"
              value={reason}
              onChange={event => setReason(event.target.value)}
              disabled={saving}
            />
          </CalendarModalField>
        ) : (
          <CalendarModalField id="event-modal-title" label={t('calendar.modal.title')}>
            <input
              type="text"
              value={title}
              onChange={event => setTitle(event.target.value)}
              disabled={saving}
            />
          </CalendarModalField>
        )}

        <CalendarModalField id="event-modal-start" label={startLabel}>
          <input
            type={isAllDay ? 'date' : 'datetime-local'}
            value={start}
            onChange={event => setStart(event.target.value)}
            disabled={saving}
          />
        </CalendarModalField>

        <CalendarModalField id="event-modal-end" label={endLabel}>
          <input
            type={isAllDay ? 'date' : 'datetime-local'}
            value={end}
            onChange={event => setEnd(event.target.value)}
            disabled={saving}
          />
        </CalendarModalField>

        {!isRestricted && (
          <CalendarModalCheckbox
            label={t('calendar.modal.allDay')}
            checked={isAllDay}
            onChange={handleAllDayChange}
            disabled={saving}
          />
        )}

        {mode === 'restricted' && (
          <CalendarModalStatus>
            {t(isAllDay ? 'calendar.modal.fullDayRestriction' : 'calendar.modal.timedRestriction')}
          </CalendarModalStatus>
        )}
      </CalendarModalContent>

      <CalendarModalActions>
        <CalendarModalButton onClick={handleSubmit} disabled={saving}>
          {submitLabel}
        </CalendarModalButton>
        {isEditing && (
          <CalendarModalButton
            variant="danger"
            onClick={() => onDelete(eventData.id)}
            disabled={saving}
          >
            {t('common.delete')}
          </CalendarModalButton>
        )}
        <CalendarModalButton onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </CalendarModalButton>
      </CalendarModalActions>
    </CalendarModalFrame>
  );
}

export default EventModal;
